// @flow
import React, {Component} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import {Toaster} from '@homeaway/react-toaster';
import AnalyticsDataLayer, {
    fragments as analyticsDataLayerFragments
} from '../AnalyticsDataLayer/AnalyticsDataLayer';
import AsyncFooter from '../AsyncFooter/AsyncFooter';
import HeaderBar from '../ConnectedHeader/ConnectedHeader';
import HtmlTitle from '../HtmlTitle/HtmlTitle';
import DropdownPanelOverlay from '../DropdownPanel/DropdownPanelOverlay';
import OverlayUpdateInterceptor from '../OverlayUpdateInterceptor/OverlayUpdateInterceptor';
import Headliner from '../Headliner/Headliner';
import ButtonBar from '../ButtonBar/ButtonBar';
import Sticky from '../Sticky/Sticky';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import SearchBar from '../SearchBar/SearchBar';
import FilterBar from '../FilterBar/FilterBar';
import FilterForm from '../FilterForm/FilterForm';
import {MAP_VIEW} from '../../constants/views';
import Map from '../Map/Map';
import Results from '../Results/Results';
import type {State as ReduxState} from '../../reducers/index';
import MapHitsDataProvider from '../MapHitsDataProvider/MapHitsDataProvider';
import SearchBannerContainer from '../SearchBannerContainer/SearchBannerContainer';
import SeoContent from '../SeoContent/SeoContent';
import SearchTermCarousel from '../SearchTermCarousel/SearchTermCarousel';
import {media} from '../../utils/breakpoints';
import {FILTERS_MODAL} from '../../actions/modals';
import routerActions from '../../actions/router';
import redirectActions from '../../actions/redirect';
import viewportActions from '../../actions/viewport';
import topBannersActions from '../../actions/topBanners';
import withWindowResize from '../withWindowResize';
import withTopBanners from '../withTopBanners/withTopBanners';
import withSkipToContentMenu from '../withSkipToContentMenu/withSkipToContentMenu';
import type {Environment} from '@homeaway/environment-resolver';
import withErrorBoundary from '../ErrorBoundary/withErrorBoundary';
import FilteredCountDataProvider from '../FilteredCountDataProvider/FilteredCountDataProvider';
import withSearchQuery from '../withSearchQuery/withSearchQuery';
import InternalTools from '../InternalTools/InternalTools';
import {isDateless} from '../../utils/searchParamsUtils';
import SeoLinkpile from '../SeoLinkpile/SeoLinkpile';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import {TripBoardModal} from '@homeaway/react-trip-board-button';

import '../../_overrides.less';
import '../../_utilities.less';
import './Application.less';
import '../../_browser.less';

import type {SearchQueryResponseData} from '../../queries/searchQuery';
import type {CallbackProps as FilteredCountDataShape} from '../FilteredCountDataProvider/FilteredCountDataProvider';
import type {State as AbacusState} from '../../reducers/abacus';
import type {SystemData} from '../../../server/utils/systemData';

type StateProps = {
    abacus: AbacusState,
    allowBanners: boolean,
    environment: Environment,
    isDatelessSearch: boolean,
    isFiltersModalShowing: boolean,
    isFullScreenMapView: boolean,
    isInternalUser: boolean,
    isMapShowing: boolean,
    isRedirecting: boolean,
    isXsScreenWidth: boolean,
    fullLocale: string,
    scrollY: number,
    showConsolidationBanner: boolean,
    showBrandTransitionBanner: string,
    showOverlay: boolean,
    siteName: string,
    ssr?: boolean,
    system: SystemData,
    topBannersHeight: number
};

type WithSearchQueryProps = {
    graphiqlUrl: string
};

type OwnProps = {
    data: SearchQueryResponseData,
    onBackToList: typeof routerActions.list,
    onResize: typeof viewportActions.sizeChanged,
    onTopBannersSizeChanged: typeof topBannersActions.sizeChanged,
    redirect: typeof redirectActions.redirect
};

export type Props = StateProps & WithSearchQueryProps & OwnProps;

const renderFilterFormWithFilteredCountData = ({
    resultsCount,
    filterGroups
}: FilteredCountDataShape) => (
    <FilterForm resultsCount={resultsCount} filterGroups={filterGroups} />
);

const mapOwnPropsToSeoContentProps = ({data: {results}}: Props) => {
    if (results) {
        const searchReviewsHeadline = results.searchReviewsHeadline;
        const searchReviews = results.searchReviews;
        if (searchReviewsHeadline && searchReviews && searchReviews.length) {
            return {
                searchReviewsHeadline,
                searchReviews
            };
        }
    }
    return null;
};

export class Application extends Component<Props> {
    componentDidUpdate({
        isFiltersModalShowing: prevIsFiltersModalShowing,
        isMapShowing: prevIsMapShowing
    }: Props) {
        const {
            data,
            isFiltersModalShowing,
            isFullScreenMapView,
            isMapShowing,
            onBackToList,
            scrollY
        } = this.props;

        // Restore the scroll position if we're coming back from map view or
        // filters modal
        if (
            typeof window !== 'undefined' &&
            ((prevIsMapShowing && !isMapShowing) ||
                (prevIsFiltersModalShowing && !isFiltersModalShowing))
        ) {
            window.scrollTo(0, scrollY);
        }

        if (isFullScreenMapView && !data.results) {
            onBackToList();
        }
    }

    render() {
        const {
            allowBanners,
            data,
            graphiqlUrl,
            isFullScreenMapView,
            isInternalUser,
            isMapShowing,
            isRedirecting,
            isXsScreenWidth,
            onBackToList,
            redirect,
            showOverlay,
            siteName,
            topBannersHeight,
            showBrandTransitionBanner
        } = this.props;

        const {loading, results, requestmarker} = data;

        const {
            alternativeLinks,
            canonicalLink,
            destination,
            headlineTitle,
            internal,
            listings,
            metaDescription,
            metaKeywords,
            pageTitle,
            pageCount,
            page,
            pidRedirectUrl,
            seoLinkpile,
            destinationMessage,
            typeaheadSuggestion,
            discoveryXploreFeeds
        } = results || {};

        if (!loading && pidRedirectUrl) {
            redirect(pidRedirectUrl);
        }

        const isResultsError = !loading && !results;
        const isRedirectingToList = isResultsError && isFullScreenMapView;
        const mapButtonDisabled = isXsScreenWidth && isResultsError;
        const classes = classNames('Application', siteName, {
            'Application--overlay': showOverlay,
            'Application--fullScreenMap': isFullScreenMapView
        });

        const applicationMapClasses = classNames('Application__map', {
            'Application__map--fullScreen': isFullScreenMapView
        });
        const topBannerMargin = {marginTop: topBannersHeight};
        const applicationMapStyles = allowBanners ? topBannerMargin : {};
        const seoContentProps = mapOwnPropsToSeoContentProps(this.props);
        const isLoadingOrRedirecting =
            loading || isRedirecting || isRedirectingToList;

        const shouldShowPreFooter = headlineTitle || !!seoContentProps;

        const showBackToList = isFullScreenMapView;

        const hasResults = !!(listings && listings.length);

        const adlFragment =
            (!isLoadingOrRedirecting &&
                !!results &&
                filterGraphQlAnywhere(
                    analyticsDataLayerFragments.results,
                    results
                )) ||
            null;

        return (
            <div className={classes} style={topBannerMargin}>
                <AnalyticsDataLayer
                    results={adlFragment}
                    requestmarker={requestmarker}
                />
                <header
                    className="Application__header"
                    data-wdio="HeaderBarContainer"
                >
                    <HtmlTitle
                        pageTitle={pageTitle}
                        metaDescription={metaDescription}
                        metaKeywords={metaKeywords}
                        canonicalLink={canonicalLink}
                        alternativeLinks={alternativeLinks}
                        page={page}
                        pageCount={pageCount}
                    />

                    <HeaderBar
                        internalTools={
                            isInternalUser ? (
                                <InternalTools
                                    searchServiceUrl={
                                        internal && internal.searchServiceUrl
                                    }
                                    graphiqlUrl={graphiqlUrl}
                                />
                            ) : null
                        }
                        showBrandTransitionBanner={showBrandTransitionBanner}
                    />
                    <SearchBar />
                </header>
                {isXsScreenWidth && (
                    <Sticky
                        className="Application__stickyButtonBarContainer"
                        top={0}
                    >
                        {({isSticky}) => (
                            <section
                                className={classNames(
                                    'Application__buttonBar Application__section--padded',
                                    {
                                        'Application__buttonBar--bordered': !isSticky,
                                        'Application__buttonBar--shadowed': isSticky
                                    }
                                )}
                            >
                                <ButtonBar
                                    mapButtonDisabled={mapButtonDisabled}
                                />
                            </section>
                        )}
                    </Sticky>
                )}
                <div className="Application__container">
                    {!isXsScreenWidth && (
                        <section className="Application__filterBar">
                            <FilterBar
                                searchResultsLoading={isLoadingOrRedirecting}
                            />
                        </section>
                    )}
                    <div
                        id="Application__resultsViewport"
                        className="Application__resultsViewport"
                    >
                        <OverlayUpdateInterceptor>
                            <div className="Application__resultsColumn">
                                <section className="Application__results Application__section--padded">
                                    <h2 className="sr-only">
                                        <FormattedMessage
                                            id="appSearchResultsSection"
                                            defaultMessage="Search results"
                                        />
                                    </h2>
                                    <ErrorBoundary>
                                        {!isLoadingOrRedirecting &&
                                            !!(
                                                typeaheadSuggestion &&
                                                pageCount &&
                                                pageCount > 0 &&
                                                discoveryXploreFeeds &&
                                                discoveryXploreFeeds.results
                                                    .length > 0
                                            ) && (
                                                <section className="Application__searchTermCarousel">
                                                    <SearchTermCarousel
                                                        discoveryXploreFeeds={
                                                            discoveryXploreFeeds
                                                        }
                                                        typeaheadSuggestion={
                                                            typeaheadSuggestion
                                                        }
                                                    />
                                                </section>
                                            )}
                                        {!isLoadingOrRedirecting &&
                                            hasResults &&
                                            destinationMessage && (
                                                <section className="Application__searchBanner">
                                                    <SearchBannerContainer
                                                        urgencyMessage={
                                                            destinationMessage
                                                        }
                                                    />
                                                </section>
                                            )}

                                        <Results
                                            results={results}
                                            loading={isLoadingOrRedirecting}
                                            allowUpdate={!isFullScreenMapView}
                                        />
                                    </ErrorBoundary>
                                </section>
                                {shouldShowPreFooter && (
                                    <section id="Application__preFooter">
                                        <div className="Application__preFooter bg-module-bg-color">
                                            <div
                                                className={classNames(
                                                    'Application__headline',
                                                    'Application__section--padded'
                                                )}
                                            >
                                                {headlineTitle && (
                                                    <Headliner
                                                        headline={headlineTitle}
                                                    />
                                                )}
                                            </div>
                                            {seoContentProps && (
                                                <div
                                                    className={
                                                        'Application__seoContent Application__section--padded'
                                                    }
                                                >
                                                    <SeoContent
                                                        {...seoContentProps}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}
                                <section
                                    className="Application__footer"
                                    role="contentinfo"
                                    data-wdio="FooterBar"
                                >
                                    <Breadcrumb destination={destination} />
                                    <SeoLinkpile {...seoLinkpile} />
                                    <AsyncFooter />
                                </section>
                            </div>

                            <section
                                className={applicationMapClasses}
                                style={applicationMapStyles}
                            >
                                <MapHitsDataProvider
                                    {...{
                                        loading: isLoadingOrRedirecting,
                                        onBackClick:
                                            showBackToList && onBackToList,
                                        options: {
                                            zoomControl:
                                                isMapShowing &&
                                                !showBackToList /* zoom conflicts with back to list */
                                        },
                                        render: (p) => <Map {...p} />,
                                        results,
                                        shouldShowFiltersButton: isXsScreenWidth,
                                        showBackToList,
                                        siteName,
                                        allowUpdate: isMapShowing
                                    }}
                                />
                            </section>
                        </OverlayUpdateInterceptor>
                        <DropdownPanelOverlay />
                    </div>
                </div>

                {isXsScreenWidth && (
                    <section className="Application__filterForm">
                        <FilteredCountDataProvider
                            searchResultsLoading={isLoadingOrRedirecting}
                            children={renderFilterFormWithFilteredCountData}
                        />
                    </section>
                )}
                <OverlayUpdateInterceptor>
                    <Toaster />
                    <TripBoardModal />
                </OverlayUpdateInterceptor>
            </div>
        );
    }
}

export const mapStateToProps = (state: ReduxState): StateProps => {
    let {
        searchParams,
        system,
        viewport: {breakpoint, scrollY},
        topBanners: {height: topBannersHeight},
        abacus,
        redirect: {isRedirecting},
        overlay: {showOverlay}
    } = state;

    const isXsScreenWidth = !media(breakpoint, {
        minWidth: 'SM_MIN'
    });

    const isSmScreenWidth =
        !media(breakpoint, {
            minWidth: 'MD_MIN'
        }) && !isXsScreenWidth;

    const isMdScreenWidth = media(breakpoint, {
        minWidth: 'MD_MIN'
    });

    const isFiltersModalShowing = searchParams.params.modal === FILTERS_MODAL;

    const isFullScreenMapView =
        searchParams.params.view === MAP_VIEW &&
        !media(breakpoint, {minWidth: 'MD_MIN'});

    const isMapShowing =
        isFullScreenMapView || media(breakpoint, {minWidth: 'MD_MIN'});

    const allowBanners =
        (isFullScreenMapView && !isSmScreenWidth) ||
        (system.isDesktop && isMdScreenWidth);

    const isInternalUser = system.isInternalUser;
    return {
        abacus,
        environment: system.environment,
        allowBanners,
        isDatelessSearch: isDateless(searchParams.params),
        isFiltersModalShowing,
        isFullScreenMapView,
        isInternalUser,
        isMapShowing,
        isRedirecting,
        isXsScreenWidth,
        fullLocale: system.fullLocale,
        topBannersHeight,
        scrollY,
        showOverlay,
        siteName: system.siteName,
        ssr: !!searchParams.params.ssr,
        showBrandTransitionBanner: system.transitionBanner,
        showConsolidationBanner:
            typeof system.consolidationBanner !== 'undefined' &&
            !system.isMobile &&
            isMdScreenWidth,
        system
    };
};

const mapDispatchToProps = {
    onBackToList: routerActions.list,
    onResize: viewportActions.sizeChanged,
    onTopBannersSizeChanged: topBannersActions.sizeChanged,
    redirect: redirectActions.redirect
};

export const applicationCompose = (ui: any) =>
    compose(
        withSearchQuery,
        connect(
            mapStateToProps,
            mapDispatchToProps
        ),
        withWindowResize,
        withErrorBoundary,
        withTopBanners,
        withSkipToContentMenu
    )(ui);

export default applicationCompose(Application);
